// src/pages/driver/useDriverAssignment.js
import { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "../../services/firebase";

function getNextDate(dateStr, frequency) {
  const [yy, mm, dd] = String(dateStr).split("-").map(Number);
  if (!yy || !mm || !dd) return "";
  const dt = new Date(Date.UTC(yy, mm - 1, dd));

  switch (frequency) {
    case "daily":
      dt.setUTCDate(dt.getUTCDate() + 1);
      break;
    case "weekly":
      dt.setUTCDate(dt.getUTCDate() + 7);
      break;
    case "fortnightly":
      dt.setUTCDate(dt.getUTCDate() + 14);
      break;
    case "monthly":
      dt.setUTCMonth(dt.getUTCMonth() + 1);
      break;
    default:
      return "";
  }

  return dt.toISOString().slice(0, 10);
}

function isStopDone(stop) {
  if (!stop) return true;
  if (stop.isWeighbridgeDocket) return true;
  if (stop.completedTime) return true;
  if (stop.skipped) return true;
  return false;
}

function isAssignmentDayComplete(assignment) {
  if (!assignment?.runs) return false;
  const allStops = Object.values(assignment.runs || {})
    .flat()
    .filter(Boolean);
  if (!allStops.length) return false;
  return allStops.every(isStopDone);
}

function getNumericUpdatedAt(value) {
  if (typeof value === "number") return value;
  if (value && typeof value === "object" && typeof value.seconds === "number") {
    return value.seconds * 1000;
  }
  if (
    value &&
    typeof value === "object" &&
    typeof value._seconds === "number"
  ) {
    return value._seconds * 1000;
  }
  return 0;
}

function normalizeBreaks(data) {
  let breaks = Array.isArray(data?.breaks) ? [...data.breaks] : [];
  if (!breaks.length && data?.breakStartedAt) {
    breaks.push({
      start: data.breakStartedAt,
      end: data.breakEndedAt || null,
      duration: data.breakDuration || null,
    });
  }
  return breaks;
}

function parseRunKey(runKey) {
  const raw = String(runKey || "").trim();

  const primary = raw.match(/^Run\s*#?\s*(\d+)$/i);
  if (primary) {
    return {
      group: 1,
      number: Number(primary[1]),
      suffix: 0,
      text: raw.toLowerCase(),
    };
  }

  const duplicate = raw.match(/^Run\s*#?\s*(\d+)\s*\((\d+)\)$/i);
  if (duplicate) {
    return {
      group: 1,
      number: Number(duplicate[1]),
      suffix: Number(duplicate[2]),
      text: raw.toLowerCase(),
    };
  }

  return {
    group: 2,
    number: Number.MAX_SAFE_INTEGER,
    suffix: 0,
    text: raw.toLowerCase(),
  };
}

function sortRunEntries(runsObj) {
  return Object.entries(runsObj || {}).sort(([a], [b]) => {
    const pa = parseRunKey(a);
    const pb = parseRunKey(b);

    if (pa.group !== pb.group) return pa.group - pb.group;
    if (pa.number !== pb.number) return pa.number - pb.number;
    if (pa.suffix !== pb.suffix) return pa.suffix - pb.suffix;
    return pa.text.localeCompare(pb.text);
  });
}

function sortRunsObject(runsObj) {
  const sorted = {};
  for (const [key, value] of sortRunEntries(runsObj || {})) {
    sorted[key] = value;
  }
  return sorted;
}

function buildRunMetaFromStops(runKey, stops, fallbackDocData) {
  const firstJob = Array.isArray(stops) ? stops.find(Boolean) : null;
  return (
    fallbackDocData?.runMeta?.[runKey] || {
      companyId: firstJob?.companyId || fallbackDocData?.companyId || "",
      companyName: firstJob?.companyName || fallbackDocData?.companyName || "",
      uiVariant: firstJob?.uiVariant || fallbackDocData?.uiVariant || "",
    }
  );
}

function stopFingerprint(stop) {
  if (!stop) return "";
  return JSON.stringify({
    jobId: stop.jobId || "",
    address: String(stop.address || "")
      .trim()
      .toLowerCase(),
    eta: stop.eta || "",
    description: stop.description || "",
    customerId: stop.customerId || "",
    siteId: stop.siteId || "",
    jobTypeId: stop.jobTypeId || "",
    jobTypeCode: stop.jobTypeCode || "",
    isWeighbridgeDocket: !!stop.isWeighbridgeDocket,
  });
}

function runFingerprint(stops) {
  if (!Array.isArray(stops)) return "";
  return stops.map(stopFingerprint).join("||");
}

function mergeDocsIntoAssignment(docs, driverId, date) {
  if (!docs.length) return null;

  const legacyId = `${driverId}_${date}`;

  const scopedDocs = docs.filter((d) => d.id !== legacyId);
  const legacyDoc = docs.find((d) => d.id === legacyId) || null;

  // Prefer scoped docs for actual runs. Legacy doc is only fallback.
  const runDocs = scopedDocs.length ? scopedDocs : legacyDoc ? [legacyDoc] : [];

  const sortedRunDocs = [...runDocs].sort((a, b) => {
    return (
      getNumericUpdatedAt(b.data?.updatedAt) -
      getNumericUpdatedAt(a.data?.updatedAt)
    );
  });

  const baseDoc = sortedRunDocs[0] || legacyDoc || docs[0];

  const merged = {
    driverId,
    driverEmail: baseDoc?.data?.driverEmail || "",
    date,
    runs: {},
    runMeta: {},
    breaks: normalizeBreaks(legacyDoc?.data || baseDoc?.data || {}),
    currentJob: legacyDoc?.data?.currentJob || baseDoc?.data?.currentJob || "",
    currentJobUpdatedAt:
      legacyDoc?.data?.currentJobUpdatedAt ||
      baseDoc?.data?.currentJobUpdatedAt ||
      null,
    recurrenceRuleId: baseDoc?.data?.recurrenceRuleId || "",
    recurrenceDate: baseDoc?.data?.recurrenceDate || date,
    recurrenceFrequency: baseDoc?.data?.recurrenceFrequency || "",
    updatedAt: baseDoc?.data?.updatedAt || Date.now(),
    sourceDocIds: docs.map((d) => d.id),
    primaryDocId: baseDoc?.id || legacyId,
  };

  const runDocMap = {};
  const runMetaMap = {};
  const seenFingerprints = new Set();

  for (const entry of sortedRunDocs) {
    const docId = entry.id;
    const data = entry.data || {};
    const docRuns = data.runs || {};

    if (!merged.driverEmail && data.driverEmail)
      merged.driverEmail = data.driverEmail;
    if (!merged.recurrenceRuleId && data.recurrenceRuleId) {
      merged.recurrenceRuleId = data.recurrenceRuleId;
    }
    if (!merged.recurrenceDate && data.recurrenceDate) {
      merged.recurrenceDate = data.recurrenceDate;
    }
    if (!merged.recurrenceFrequency && data.recurrenceFrequency) {
      merged.recurrenceFrequency = data.recurrenceFrequency;
    }

    for (const [runKey, stops] of Object.entries(docRuns)) {
      if (!Array.isArray(stops) || !stops.length) continue;

      const fingerprint = `${runKey}::${runFingerprint(stops)}`;

      // If same run already exists from another doc, skip it completely
      if (seenFingerprints.has(fingerprint)) continue;
      seenFingerprints.add(fingerprint);

      let finalRunKey = runKey;

      // Only rename if same run name exists but content is actually different
      if (merged.runs[finalRunKey]) {
        const existingFingerprint = runFingerprint(merged.runs[finalRunKey]);
        const incomingFingerprint = runFingerprint(stops);

        if (existingFingerprint === incomingFingerprint) {
          continue;
        }

        let i = 1;
        while (merged.runs[`${runKey} (${i})`]) i += 1;
        finalRunKey = `${runKey} (${i})`;
      }

      merged.runs[finalRunKey] = stops;
      runDocMap[finalRunKey] = docId;
      runMetaMap[finalRunKey] = buildRunMetaFromStops(runKey, stops, data);
    }
  }

  merged.runs = sortRunsObject(merged.runs);
  merged.runMeta = sortRunsObject(runMetaMap);
  merged.runDocMap = { ...runDocMap };

  return merged;
}

async function createAssignmentsForDateFromRule({ ruleId, ruleDoc, dateStr }) {
  const driverId = ruleDoc?.driverId;
  const driverEmail = ruleDoc?.driverEmail || "";
  const targets = Array.isArray(ruleDoc?.targets) ? ruleDoc.targets : [];
  if (!driverId || !dateStr || !targets.length) return;

  const combinedRunsWithIds = {};
  const combinedRunMeta = {};
  const perCompany = {};

  for (const t of targets) {
    const companyId = t.companyId;
    const companyName = t.companyName || "";
    const uiVariant = t.uiVariant || "default";
    const runsTemplate = t.runsTemplate || {};
    if (!companyId) continue;

    if (!perCompany[companyId]) {
      perCompany[companyId] = { runsWithIds: {}, uiVariant, companyName };
    }

    for (const [runKey, stops] of Object.entries(runsTemplate)) {
      if (!combinedRunsWithIds[runKey]) combinedRunsWithIds[runKey] = [];
      if (!combinedRunMeta[runKey]) {
        combinedRunMeta[runKey] = { companyId, companyName, uiVariant };
      }
      if (!perCompany[companyId].runsWithIds[runKey]) {
        perCompany[companyId].runsWithIds[runKey] = [];
      }

      for (const stop of stops || []) {
        const jobPayload = {
          companyId,
          companyName,
          driverId,
          driverEmail,
          date: dateStr,
          runKey,
          uiVariant,

          address: stop.address || "",
          eta: stop.eta || "",
          description: stop.description || "",
          poNumber: stop.poNumber || "",
          siteContact: stop.siteContact || "",
          sitePhone: stop.sitePhone || "",
          siteEmail: stop.siteEmail || "",
          fixed: !!stop.fixed,

          customerId: stop.customerId || "",
          customerName: stop.customerName || "",
          siteId: stop.siteId || "",
          siteLabel: stop.siteLabel || "",
          jobTypeId: stop.jobTypeId || "",
          jobTypeCode: stop.jobTypeCode || "",
          jobTypeName: stop.jobTypeName || "",

          recurrenceRuleId: ruleId,
          recurrenceDate: dateStr,

          status: "assigned",
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };

        const ref = await addDoc(collection(db, "jobs"), jobPayload);

        const stopWithId = {
          jobId: ref.id,
          address: jobPayload.address,
          eta: jobPayload.eta,
          description: jobPayload.description,
          poNumber: jobPayload.poNumber,
          siteContact: jobPayload.siteContact,
          sitePhone: jobPayload.sitePhone,
          siteEmail: jobPayload.siteEmail,
          fixed: jobPayload.fixed,

          jobTypeId: jobPayload.jobTypeId,
          jobTypeCode: jobPayload.jobTypeCode,
          jobTypeName: jobPayload.jobTypeName,
          customerId: jobPayload.customerId,
          customerName: jobPayload.customerName,
          siteId: jobPayload.siteId,
          siteLabel: jobPayload.siteLabel,
        };

        combinedRunsWithIds[runKey].push(stopWithId);
        perCompany[companyId].runsWithIds[runKey].push(stopWithId);
      }
    }
  }

  const combinedId = `${driverId}_${dateStr}`;
  await setDoc(
    doc(db, "assignments", combinedId),
    {
      driverId,
      driverEmail,
      date: dateStr,
      runs: sortRunsObject(combinedRunsWithIds),
      runMeta: sortRunsObject(combinedRunMeta),
      uiVariant: "mixed",
      updatedAt: Date.now(),
      recurrenceRuleId: ruleId,
      recurrenceDate: dateStr,
    },
    { merge: false },
  );

  for (const [companyId, data] of Object.entries(perCompany)) {
    const scopedId = `${companyId}_${driverId}_${dateStr}`;
    const scopedRunMeta = {};
    for (const [runKey, stops] of Object.entries(data.runsWithIds || {})) {
      scopedRunMeta[runKey] = buildRunMetaFromStops(runKey, stops, {
        companyId,
        companyName: data.companyName || "",
        uiVariant: data.uiVariant || "default",
      });
    }

    await setDoc(
      doc(db, "assignments", scopedId),
      {
        companyId,
        companyName: data.companyName || "",
        driverId,
        driverEmail,
        date: dateStr,
        runs: sortRunsObject(data.runsWithIds),
        runMeta: sortRunsObject(scopedRunMeta),
        uiVariant: data.uiVariant || "default",
        updatedAt: Date.now(),
        recurrenceRuleId: ruleId,
        recurrenceDate: dateStr,
      },
      { merge: false },
    );
  }
}

export default function useDriverAssignment(date) {
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onBreak, setOnBreak] = useState(false);
  const [breakHistory, setBreakHistory] = useState([]);

  const user = getAuth().currentUser;
  const legacyDocId = `${user?.uid}_${date}`;
  const legacyDocRef = doc(db, "assignments", legacyDocId);

  const getMinutesDiff = (start, end) => {
    if (!start || !end) return null;
    const [sh, sm] = String(start).split(":").map(Number);
    const [eh, em] = String(end).split(":").map(Number);
    if (
      Number.isNaN(sh) ||
      Number.isNaN(sm) ||
      Number.isNaN(eh) ||
      Number.isNaN(em)
    ) {
      return null;
    }
    let diff = eh * 60 + em - (sh * 60 + sm);
    if (diff < 0) diff += 24 * 60;
    return diff;
  };

  const fetchAssignment = async () => {
    if (!user || !date) return;

    setLoading(true);
    try {
      const q = query(
        collection(db, "assignments"),
        where("driverId", "==", user.uid),
        where("date", "==", date),
      );

      const qs = await getDocs(q);
      let docs = qs.docs.map((d) => ({ id: d.id, data: d.data() }));

      const legacyId = `${user.uid}_${date}`;
      const scopedDocs = docs.filter((d) => d.id !== legacyId);
      const legacyDoc = docs.find((d) => d.id === legacyId) || null;

      // If scoped docs exist, keep legacy only as metadata fallback.
      // This stops duplicated runs from combined+scoped docs.
      if (scopedDocs.length) {
        docs = legacyDoc ? [...scopedDocs, legacyDoc] : scopedDocs;
      }

      if (!docs.length) {
        const legacySnap = await getDoc(legacyDocRef);
        if (legacySnap.exists()) {
          docs = [{ id: legacySnap.id, data: legacySnap.data() }];
        }
      }

      if (!docs.length) {
        setAssignment(null);
        setBreakHistory([]);
        setOnBreak(false);
        return;
      }

      const merged = mergeDocsIntoAssignment(docs, user.uid, date);
      const breaks = normalizeBreaks(merged || {});
      const last = breaks[breaks.length - 1];
      const currentlyOnBreak = !!(last && !last.end);

      setAssignment({
        ...(merged || null),
        breaks,
      });
      setBreakHistory(breaks);
      setOnBreak(currentlyOnBreak);
    } catch (err) {
      console.error("Failed to fetch driver assignment:", err);
      setAssignment(null);
      setBreakHistory([]);
      setOnBreak(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || !date) return;
    fetchAssignment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, user?.uid]);

  useEffect(() => {
    if (!user || !date || !assignment) return;

    const ruleId = assignment.recurrenceRuleId;
    const recurrenceDate = assignment.recurrenceDate || assignment.date;
    if (!ruleId) return;
    if (recurrenceDate !== date) return;
    if (!isAssignmentDayComplete(assignment)) return;

    (async () => {
      try {
        const ruleRef = doc(db, "recurrenceRules", ruleId);

        const txResult = await runTransaction(db, async (tx) => {
          const ruleSnap = await tx.get(ruleRef);
          if (!ruleSnap.exists()) return { ok: false, reason: "missing_rule" };

          const rule = ruleSnap.data();
          if (!rule?.active) return { ok: false, reason: "inactive" };

          if (rule.currentDate !== date) {
            return { ok: false, reason: "already_advanced" };
          }

          const nextDate = getNextDate(rule.currentDate, rule.frequency);
          if (!nextDate) return { ok: false, reason: "bad_frequency" };

          if (rule.endDate && nextDate > rule.endDate) {
            tx.update(ruleRef, {
              active: false,
              endedAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
            return { ok: false, reason: "ended" };
          }

          const instanceRef = doc(
            db,
            "recurrenceInstances",
            `${ruleId}_${nextDate}`,
          );
          const instanceSnap = await tx.get(instanceRef);
          if (instanceSnap.exists()) {
            tx.update(ruleRef, {
              currentDate: nextDate,
              updatedAt: serverTimestamp(),
            });
            return { ok: false, reason: "instance_exists" };
          }

          tx.set(instanceRef, {
            ruleId,
            date: nextDate,
            createdAt: serverTimestamp(),
            createdByDriver: user.uid,
          });

          tx.update(ruleRef, {
            currentDate: nextDate,
            updatedAt: serverTimestamp(),
            lastAdvancedBy: user.uid,
          });

          return { ok: true, nextDate, rule };
        });

        if (!txResult?.ok) return;

        await createAssignmentsForDateFromRule({
          ruleId,
          ruleDoc: txResult.rule,
          dateStr: txResult.nextDate,
        });
      } catch (e) {
        console.error("Auto recurrence advance failed:", e);
      }
    })();
  }, [assignment, user, date]);

  const handleBreakToggle = async () => {
    if (!user) return;

    const now = new Date().toLocaleTimeString("en-AU", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "Australia/Melbourne",
    });

    const current = assignment || {};
    let breaks = Array.isArray(current.breaks) ? [...current.breaks] : [];

    if (!onBreak) {
      const newBreak = { start: now, end: null, duration: null };
      breaks.push(newBreak);

      const updated = {
        breaks,
        breakStartedAt: newBreak.start,
        breakEndedAt: null,
        breakDuration: null,
        updatedAt: Date.now(),
      };

      setAssignment((prev) => ({ ...(prev || {}), ...updated }));
      setBreakHistory(breaks);
      setOnBreak(true);

      await setDoc(
        legacyDocRef,
        {
          driverId: user.uid,
          driverEmail: user.email || "",
          date,
          ...updated,
        },
        { merge: true },
      );
    } else {
      if (!breaks.length) {
        breaks = [{ start: now, end: now, duration: "0 mins" }];
      } else {
        const lastIndex = breaks.length - 1;
        const last = breaks[lastIndex];
        const start = last.start || now;
        const minutes = getMinutesDiff(start, now);
        const durationLabel = minutes != null ? `${minutes} mins` : null;
        breaks[lastIndex] = {
          ...last,
          start,
          end: now,
          duration: durationLabel,
        };
      }

      const last = breaks[breaks.length - 1];

      const updated = {
        breaks,
        breakStartedAt: last.start,
        breakEndedAt: last.end,
        breakDuration: last.duration,
        updatedAt: Date.now(),
      };

      setAssignment((prev) => ({ ...(prev || {}), ...updated }));
      setBreakHistory(breaks);
      setOnBreak(false);

      await setDoc(
        legacyDocRef,
        {
          driverId: user.uid,
          driverEmail: user.email || "",
          date,
          ...updated,
        },
        { merge: true },
      );
    }
  };

  const handleTimestamp = async (runKey, index, field) => {
    if (!assignment) return;

    const now = new Date().toLocaleTimeString("en-AU", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "Australia/Melbourne",
    });

    const updatedAssignment = JSON.parse(JSON.stringify(assignment));
    const item = updatedAssignment.runs?.[runKey]?.[index];
    if (!item) return;

    item[field] = now;

    if (field === "completedTime" && item.startJobAt) {
      const minutes = getMinutesDiff(item.startJobAt, now);
      if (minutes != null) item.duration = `${minutes} mins`;
    }

    updatedAssignment.runs = sortRunsObject(updatedAssignment.runs || {});
    setAssignment(updatedAssignment);

    const targetDocId =
      assignment?.runDocMap?.[runKey] ||
      assignment?.primaryDocId ||
      legacyDocId;

    const targetRef = doc(db, "assignments", targetDocId);
    const targetSnap = await getDoc(targetRef);
    const targetData = targetSnap.exists() ? targetSnap.data() : {};

    const nextRuns = { ...(targetData.runs || {}) };
    const nextRun = Array.isArray(nextRuns[runKey])
      ? [...nextRuns[runKey]]
      : [];
    const nextItem = { ...(nextRun[index] || {}) };

    nextItem[field] = now;
    if (field === "completedTime" && nextItem.startJobAt) {
      const minutes = getMinutesDiff(nextItem.startJobAt, now);
      if (minutes != null) nextItem.duration = `${minutes} mins`;
    }

    nextRun[index] = nextItem;
    nextRuns[runKey] = nextRun;

    await setDoc(
      targetRef,
      {
        driverId: user.uid,
        driverEmail: user.email || "",
        date,
        runs: sortRunsObject(nextRuns),
        updatedAt: Date.now(),
      },
      { merge: true },
    );

    if (field === "completedTime" && item.siteEmail) {
      const emailId = `${user.uid}_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2)}`;

      await setDoc(doc(db, "mail", emailId), {
        to: item.siteEmail,
        message: {
          subject: `✅ Job Completed – ${item.address}`,
          text: `The job at ${item.address} has been completed.`,
          html: `
            <p>Hello,</p>
            <p>The job at <strong>${item.address}</strong> has been marked as <strong>completed</strong>.</p>
            <p><strong>Duration:</strong> ${item.duration || "N/A"}</p>
            <p>Thank you.<br/>Greenspace Team</p>
          `,
        },
      });
    }
  };

  const refresh = async () => {
    await fetchAssignment();
  };

  return {
    assignment,
    loading,
    onBreak,
    breakHistory,
    handleBreakToggle,
    handleTimestamp,
    refresh,
    assignmentDocId: assignment?.primaryDocId || legacyDocId,
    runDocMap: assignment?.runDocMap || {},
    sourceDocIds: assignment?.sourceDocIds || [],
  };
}
